import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiFileText } = FiIcons;

const Terms = () => {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 pt-24">
      <div className="w-full max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-6 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} />
            Back to Home
          </Link>

          <div className="bg-gray-800/50 backdrop-blur-xl rounded-3xl p-8 border border-cyan-400/20 shadow-[0_0_50px_rgba(0,255,255,0.1)]">
            <div className="flex items-center gap-3 mb-6">
              <SafeIcon icon={FiFileText} className="text-4xl text-cyan-400" />
              <h1 className="text-4xl font-bold text-white">Terms of Service</h1>
            </div>

            <div className="space-y-6 text-gray-300">
              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-3">1. Acceptance of Terms</h2>
                <p>
                  By accessing and using Vidroid ("the Service"), you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-3">2. Description of Service</h2>
                <p>
                  Vidroid is an AI-powered video generation platform that utilizes multiple AI models through Apimart and Kie.AI APIs. The Service provides access to:
                </p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li><strong>Apimart Models:</strong> Sora 2, Veo 3.1 Fast, and MiniMax-Hailuo-02</li>
                  <li><strong>Kie.AI Models:</strong> Grok Imagine, Hailuo 2.3, Wan 2.5, and Runway Aleph</li>
                </ul>
                <p className="mt-3">
                  The Service is provided "as is" and may be modified, suspended, or discontinued at any time without notice.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-3">3. API Key Requirements</h2>
                <p>
                  Users must provide their own API keys to use the Service:
                </p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li><strong>Apimart API Key:</strong> Required for Sora 2, Veo 3.1, and MiniMax models</li>
                  <li><strong>Kie.AI API Key:</strong> Required for Grok Imagine, Hailuo 2.3, Wan 2.5, and Runway Aleph models</li>
                </ul>
                <p className="mt-3">You are solely responsible for:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Obtaining and maintaining your API keys</li>
                  <li>Keeping your API keys secure and confidential</li>
                  <li>All activities that occur under your API keys</li>
                  <li>Any costs associated with your API usage</li>
                  <li>Complying with both Apimart's and Kie.AI's terms of service and usage policies</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-3">4. Data Storage and Security</h2>
                <p>
                  Your API keys are stored securely in your browser's localStorage:
                </p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>API keys remain exclusively on your device</li>
                  <li>Keys are not transmitted to our servers or any third parties (except the respective API providers)</li>
                  <li>You are responsible for the security of your device and browser</li>
                  <li>You can clear your API keys at any time through browser settings</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-3">5. Affiliate Disclosure</h2>
                <p>
                  This website may contain affiliate links to third-party services, including but not limited to Apimart and Kie.AI. This means we may earn a commission if you click on these links and make a purchase or sign up for services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-3">6. User Responsibilities</h2>
                <p>You agree to:</p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>Use the Service only for lawful purposes</li>
                  <li>Not generate content that is illegal, harmful, or objectionable</li>
                  <li>Not use the Service to infringe on intellectual property rights</li>
                  <li>Comply with all applicable laws and regulations</li>
                  <li>Respect the terms of service of both Apimart and Kie.AI</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-3">7. Contact Us</h2>
                <p>
                  If you have questions about these Terms of Service, please contact us:
                </p>
                <ul className="list-none ml-4 mt-2 space-y-2">
                  <li><strong>Email:</strong> <a href="mailto:fiverrings@gmail.com" className="text-cyan-400 hover:text-cyan-300">fiverrings@gmail.com</a></li>
                  <li><strong>Contact Form:</strong> <a href="https://fiverring.com/#/contact" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">https://fiverring.com/#/contact</a></li>
                </ul>
              </section>

              <div className="mt-8 pt-6 border-t border-gray-700">
                <p className="text-sm text-gray-400">
                  <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                <p className="text-sm text-gray-400 mt-2">
                  <strong>Effective Date:</strong> January 1, 2025
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Terms;